import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'

interface Props {
  title?: string
  action?: ReactNode
  children: ReactNode
  /** For tables and lists that should meet the card's edges. */
  flush?: boolean
  /** Takes the rest of a `<Page fill>` and scrolls its own body, so the card's
   *  header stays put while the list moves under it. */
  fill?: boolean
  className?: string
}

export function Card({ title, action, children, flush = false, fill = false, className }: Props) {
  const hasHead = Boolean(title || action)

  return (
    <section
      className={cx(
        'flex flex-col overflow-hidden rounded-lg border border-line-soft bg-surface',
        fill && 'min-h-0 lg:flex-1',
        className,
      )}
    >
      {hasHead && (
        // No rule under the title: what follows brings its own top edge, and a
        // divider on top of that is two lines doing one job.
        // `[&_a]:min-h-touch`: the "All patients" link in this slot is a 23px
        // target inside a 52px header, and the header has the room to give it
        // the 44px floor without growing.
        <header className="flex min-h-13 flex-none flex-wrap items-center justify-between gap-x-3 gap-y-2 px-5 pt-4 pb-3 [&_a]:inline-flex [&_a]:min-h-touch [&_a]:items-center">
          {title && <h2 className="min-w-0 text-lg font-semibold tracking-snug">{title}</h2>}
          {action}
        </header>
      )}
      <div
        className={cx(
          !flush && cx('p-5 [&>*+*]:mt-4', hasHead && 'pt-2'),
          fill && 'min-h-0 flex-1 overflow-y-auto',
        )}
      >
        {children}
      </div>
    </section>
  )
}
